import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../store/auth';
import { apiFetch } from './utils';
import type { KVListResult, KVValueResult, SearchResult } from './types';

const API_BASE_URL = 'http://localhost:8787/api/storage';

export async function fetchKeys(
  namespaceId: string,
  options: {
    prefix?: string;
    cursor?: string;
    limit?: number;
  } = {},
): Promise<KVListResult> {
  const { prefix, cursor, limit } = options;
  const params = new URLSearchParams();

  if (prefix) params.append('prefix', prefix);
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());

  const url = `${API_BASE_URL}/namespaces/${namespaceId}/keys${params.toString() ? `?${params.toString()}` : ''}`;

  return apiFetch(url, {
    method: 'GET',
  });
}

export async function fetchValue(
  namespaceId: string,
  key: string,
): Promise<KVValueResult> {
  const url = `${API_BASE_URL}/namespaces/${namespaceId}/values/${encodeURIComponent(key)}`;

  const data = await apiFetch(url, {
    method: 'GET',
  });

  return data as KVValueResult;
}

export function useKeys(
  namespaceId: string,
  options: {
    prefix?: string;
    cursor?: string;
    limit?: number;
  } = {},
) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['keys', namespaceId, options],
    queryFn: () => fetchKeys(namespaceId, options),
    enabled: isAuthenticated && !!namespaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    placeholderData: (prev) => prev,
  });
}

export function useKeyValue(namespaceId: string, key: string) {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['keyValue', namespaceId, key],
    queryFn: () => fetchValue(namespaceId, key),
    enabled: isAuthenticated && !!namespaceId && !!key,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

export async function searchAllNamespaces(
  searchTerm: string,
  options: {
    includeValues?: boolean;
    limit?: number;
    exactKey?: boolean;
  } = {},
): Promise<SearchResult> {
  const { includeValues = true, limit = 20, exactKey = false } = options;
  const params = new URLSearchParams();
  
  params.append('query', searchTerm);
  if (includeValues) params.append('include_values', 'true');
  if (limit) params.append('limit', limit.toString());
  if (exactKey) params.append('exact_key', 'true');

  const url = `${API_BASE_URL}/search?${params.toString()}`;
  
  return apiFetch(url, {
    method: 'GET',
  });
}

export function useSearch(
  searchTerm: string,
  options: {
    includeValues?: boolean;
    limit?: number;
    enabled?: boolean;
    exactKey?: boolean;
  } = {},
) {
  const { isAuthenticated } = useAuthStore();
  const { enabled = true, ...searchOptions } = options;

  return useQuery({
    queryKey: ['search', searchTerm, searchOptions],
    queryFn: () => searchAllNamespaces(searchTerm, searchOptions),
    enabled: isAuthenticated && !!searchTerm && searchTerm.length >= 2 && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: false,
  });
}
